import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Button, Form, InputNumber, Modal, Switch, Table, Tabs } from "antd"
import { ColumnsType } from "antd/lib/table"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import {
  BONUS_SPEND_TIER_COUNT,
  BonusSpendMission,
  BonusSpendMissionTier,
  GamePlatformType,
  NativeGame,
} from "../store/types"
import { validateBonusTiers } from "../helper/bonus-missions-validation"

type Props = {
  game: NativeGame | null
  onClose: () => void
}

type RowState = BonusSpendMissionTier & { id?: string }

const DEFAULT_ROWS = (): RowState[] => Array.from(
  { length: BONUS_SPEND_TIER_COUNT },
  (_, idx) => ({
    level: idx + 1,
    spendThresholdUsd: 0,
    rewardUsd: 0,
    enabled: true,
  }),
)

const PLATFORM_LABELS: Record<GamePlatformType, string> = {
  ANDROID: "Android",
  IOS: "iOS",
}

export const BonusMissionsModal: FC<Props> = observer(({ game, onClose }) => {
  const { dataStore: { nativeGamesControlStore } } = useStore()
  // One tier-set per platform. Each tab loads + saves independently so an
  // admin can configure ANDROID without touching IOS rows.
  const [rowsByPlatform, setRowsByPlatform] = useState<Record<GamePlatformType, RowState[]>>({
    ANDROID: DEFAULT_ROWS(),
    IOS: DEFAULT_ROWS(),
  })
  const [activePlatform, setActivePlatform] = useState<GamePlatformType>(GamePlatformType.ANDROID)
  const [loadingPlatform, setLoadingPlatform] = useState<GamePlatformType | null>(null)
  const [savingPlatform, setSavingPlatform] = useState<GamePlatformType | null>(null)

  const load = useCallback(async(gameId: string, platform: GamePlatformType) => {
    setLoadingPlatform(platform)
    try {
      const data = await nativeGamesControlStore.getBonusSpendMissions(gameId, platform)
      const byLevel: Record<number, BonusSpendMission> = {}
      data.forEach(row => {
        byLevel[row.level] = row
      })
      const next = DEFAULT_ROWS().map(r => ({
        id: byLevel[r.level]?.id,
        level: r.level,
        spendThresholdUsd: byLevel[r.level]?.spendThresholdUsd ?? 0,
        rewardUsd: byLevel[r.level]?.rewardUsd ?? 0,
        enabled: byLevel[r.level]?.enabled ?? true,
      }))
      setRowsByPlatform(prev => ({ ...prev, [platform]: next }))
    } catch (err: any) {
      notifications.error(err?.message || "Failed to load bonus missions")
    } finally {
      setLoadingPlatform(null)
    }
  }, [nativeGamesControlStore])

  useEffect(() => {
    if (game) {
      void load(game.id, activePlatform)
    }
  }, [game, activePlatform, load])

  if (!game) return null

  const updateRow = (platform: GamePlatformType, level: number, patch: Partial<RowState>) => {
    setRowsByPlatform(prev => ({
      ...prev,
      [platform]: prev[platform].map(r => (r.level === level ? { ...r, ...patch } : r)),
    }))
  }

  const onSubmit = async() => {
    const rows = rowsByPlatform[activePlatform]
    const tiers: BonusSpendMissionTier[] = rows.map(r => ({
      level: r.level,
      spendThresholdUsd: Number(r.spendThresholdUsd),
      rewardUsd: Number(r.rewardUsd),
      enabled: r.enabled,
    }))
    const err = validateBonusTiers(tiers)
    if (err) {
      notifications.error(err.message)

      return
    }
    setSavingPlatform(activePlatform)
    try {
      await nativeGamesControlStore.setBonusSpendMissions(game.id, activePlatform, tiers)
      notifications.success(`Bonus missions saved (${PLATFORM_LABELS[activePlatform]})`)
      onClose()
    } catch (e: any) {
      notifications.error(e?.message || "Failed to save bonus missions")
    } finally {
      setSavingPlatform(null)
    }
  }

  const renderColumns = (platform: GamePlatformType): ColumnsType<RowState> => [
    { title: "Level", dataIndex: "level", key: "level", width: 80 },
    {
      title: "Spend Threshold (USD)",
      dataIndex: "spendThresholdUsd",
      key: "spendThresholdUsd",
      render: (_: number, row: RowState) => (
        <Form.Item style={{ margin: 0 }}>
          <InputNumber
            min={0}
            step={0.01}
            value={row.spendThresholdUsd}
            onChange={value => updateRow(platform, row.level, { spendThresholdUsd: Number(value ?? 0) })}
          />
        </Form.Item>
      ),
    },
    {
      title: "Reward (USD)",
      dataIndex: "rewardUsd",
      key: "rewardUsd",
      render: (_: number, row: RowState) => (
        <Form.Item style={{ margin: 0 }}>
          <InputNumber
            min={0}
            step={0.01}
            value={row.rewardUsd}
            onChange={value => updateRow(platform, row.level, { rewardUsd: Number(value ?? 0) })}
          />
        </Form.Item>
      ),
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 100,
      render: (_: boolean, row: RowState) => (
        <Switch
          checked={row.enabled}
          onChange={checked => updateRow(platform, row.level, { enabled: checked })}
        />
      ),
    },
  ]

  const tabItems = (Object.values(GamePlatformType) as GamePlatformType[]).map(platform => ({
    key: platform,
    label: PLATFORM_LABELS[platform],
    children: (
      <Table<RowState>
        dataSource={rowsByPlatform[platform]}
        columns={renderColumns(platform)}
        rowKey="level"
        pagination={false}
        loading={loadingPlatform === platform}
        bordered
        size="small"
      />
    ),
  }))

  return (
    <Modal
      open
      onCancel={onClose}
      title={`Bonus Spend Missions — ${game.name}`}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          loading={savingPlatform === activePlatform}
          onClick={onSubmit}
        >
          {`Save ${PLATFORM_LABELS[activePlatform]}`}
        </Button>,
      ]}
    >
      <p style={{ marginBottom: 12 }}>
        Configure 4 tiers per platform. Spend thresholds must strictly increase. Rewards must be greater than 0.
      </p>
      <Tabs
        activeKey={activePlatform}
        onChange={key => setActivePlatform(key as GamePlatformType)}
        items={tabItems}
      />
    </Modal>
  )
})
